import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications, useMarkNotificationsRead, useDeleteNotification } from '../hooks/useNotifications';
import { Notification } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
}

export function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { data: notificationsData, isLoading, refetch } = useNotifications();
  const markAsRead = useMarkNotificationsRead();
  const deleteNotification = useDeleteNotification();
  const { isAuthenticated } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);

  const response = notificationsData as unknown as NotificationsResponse;
  const notifications = Array.isArray(response?.notifications) ? response.notifications : [];
  const unreadCount = typeof response?.unreadCount === 'number' ? response.unreadCount : 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    refetch();
    if (unreadCount > 0) {
      markAsRead.mutate();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDeleteNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();

    if (deleteNotification.isPending) {
      return;
    }

    deleteNotification.mutate(notificationId, {
      onSuccess: () => {
        refetch();
      },
      onError: (error: any) => {
        console.error('Failed to delete notification:', error);
      },
    });
  };

  const displayedNotifications = showAll ? notifications : notifications.slice(0, 10);

  if (!isAuthenticated) return null;

  return (
    <div className="relative">
      <button
        onClick={handleOpen}
        className="relative p-2 text-white hover:text-green-200 focus:outline-none"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleClose} />

          <div
            ref={modalRef}
            className="fixed left-1/2 -translate-x-1/2 top-16 w-96 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              {isLoading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">No notifications</div>
              ) : (
                <div className="max-h-96 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                  {displayedNotifications.map((notification) => {
                    const username = notification.content.split(' ')[0];
                    const restOfContent = notification.content.split(' ').slice(1).join(' ');

                    return (
                      <div
                        key={notification._id}
                        className={`py-2 px-4 relative group ${
                          !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification._id)}
                          disabled={deleteNotification.isPending}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          title="Delete notification"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <p className="text-sm text-gray-900 dark:text-white pr-8">
                          <Link
                            to={`/user/${username}`}
                            className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {username}
                          </Link>
                          {' '}{restOfContent}
                        </p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(notification.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    );
                  })}
                  {notifications.length > 10 && !showAll && (
                    <button
                      onClick={() => setShowAll(true)}
                      className="mt-2 w-full rounded-md bg-gray-100 dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Show More
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
